import { Box, Stack } from '@mui/material';
import moment from 'moment';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { APP, ROUTES } from 'config';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form-latest';
import {
  AutocompleteInput,
  Button,
  DataLossPrompt,
  DateTimeInput,
  Footer,
  Panel,
  SelectInput,
  SortableList,
  SwitchInput,
  TextField,
  TextInput,
  Typography,
} from 'melp-design/components';
import {
  ContentLanguage,
  currencies,
  Currency,
  uppercaseContentLanguages,
} from 'types/general';
import {
  DepositManagementMethod,
  depositManagementMethods,
  useAdminClient,
  useAdminUpdateClient,
} from 'store/admin-clients';
import { useRouteParams } from 'utils/useIdParam';
import { formatCountry, formatLanguage } from 'utils/format';
import { usePredefinedToasts } from 'utils/Toast';
import { CompanyStatus, companyStatuses } from 'store/companies';
import { AuthProvider, authProviders } from 'store/auth';
import { Delete } from '@mui/icons-material';
import { UnlinkAppUsers } from './unlink-app-users.component';

interface Values {
  status: CompanyStatus;
  creationDate: string;
  country: string;
  name: string;
  code: string;
  defaultCurrency: Currency;
  internalComment: string;
  billingInfo: string;
  depositManagementMethod: DepositManagementMethod;
  useExternalId: boolean;
  authProvider: AuthProvider;
  authProviderName: string;
  sendEmployeeWelcomeMessage: boolean;
  accountantEmails: Array<{ email: string }>;
  accountantLanguage: Uppercase<ContentLanguage>;
}

const INITIAL_VALUES: Values = {
  status: 'active',
  creationDate: '',
  country: '',
  name: '',
  code: '',
  defaultCurrency: APP.currencies.default,
  internalComment: '',
  billingInfo: '',
  depositManagementMethod: 'company',
  sendEmployeeWelcomeMessage: true,
  useExternalId: false,
  authProvider: 'cognito',
  authProviderName: '',
  accountantEmails: [{ email: '' }],
  accountantLanguage: 'EN',
};

export const AdminClientSettings = () => {
  const { t, i18n } = useTranslation();
  const { id: clientId } = useRouteParams(
    ROUTES.admin.clients.details.settings,
  );
  const predefinedToasts = usePredefinedToasts();

  const { data: client } = useAdminClient(clientId);

  const { mutate: updateClient, isLoading: isUpdating } =
    useAdminUpdateClient();

  const {
    reset,
    handleSubmit,
    watch,
    control,
    formState: { dirtyFields },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });
  const {
    fields: accountantEmailsFields,
    append: appendAccountantEmail,
    remove: removeAccountantEmail,
  } = useFieldArray({
    control,
    name: 'accountantEmails',
  });

  const isDirty = !!Object.keys(dirtyFields).length;

  useEffect(() => {
    if (client) {
      reset({
        name: client.name,
        creationDate: moment(client.creationDate).format('l LT'),
        country: client.country,
        code: client.code,
        defaultCurrency: client.defaultCurrency,
        internalComment: client.internalComment,
        billingInfo: client.billingInfo,
        status: client.status,
        depositManagementMethod: client.settings.depositManagementMethod,
        useExternalId: client.settings.useExternalId,
        authProvider: client.settings.authProvider,
        authProviderName: client.settings.authProviderName,
        sendEmployeeWelcomeMessage: client.settings.sendEmployeeWelcomeMessage,
        accountantEmails: client.settings.accountantEmails.map((email) => ({
          email,
        })),
        accountantLanguage: client.settings.accountantLanguage,
      });
    }
  }, [client, reset]);

  const [authProvider] = watch(['authProvider']);

  if (!client) return null;

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateClient(
      {
        clientId,
        data: {
          name: values.name,
          code: values.code,
          defaultCurrency: values.defaultCurrency,
          country: values.country,
          internalComment: values.internalComment,
          billingInfo: values.billingInfo,
          depositManagementMethod: values.depositManagementMethod,
          useExternalId: values.useExternalId,
          deactivationDate:
            values.status === 'inactive' ? moment().toISOString() : null,
          authProvider: values.authProvider,
          authProviderName: values.authProviderName,
          sendEmployeeWelcomeMessage: values.sendEmployeeWelcomeMessage,
          accountantEmails: values.accountantEmails.map(({ email }) => email),
          accountantLanguage: values.accountantLanguage,
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
        },
      },
    );
  };

  return (
    <>
      <Stack>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Panel title={t('sidebar.administratorsInformation')}>
              <Stack spacing={2}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SelectInput
                        label={t('common.status')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={companyStatuses.map((status) => ({
                          label: t(`status.${status}`),
                          value: status,
                        }))}
                        required
                        ref={ref}
                      />
                    );
                  }}
                />

                <Controller
                  name="creationDate"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <DateTimeInput
                      label={t('clients.creationDate')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name="country"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <AutocompleteInput
                        label={t('benefits.country')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={APP.countries.alpha3.map((alpha3) => ({
                          value: alpha3,
                          label: formatCountry(i18n.language, alpha3),
                        }))}
                        required
                        ref={ref}
                      />
                    );
                  }}
                />

                <Controller
                  name="name"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <TextField
                        label={t('companies.company_name')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        required
                        ref={ref}
                      />
                    );
                  }}
                />

                <Controller
                  name="code"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <TextField
                        label={t('companies.company_code')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        required
                        ref={ref}
                      />
                    );
                  }}
                />

                <Controller
                  name="defaultCurrency"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SelectInput
                        label={t('clients.default_currency')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={currencies.map((currency) => ({
                          label: currency,
                          value: currency,
                        }))}
                        required
                        ref={ref}
                      />
                    );
                  }}
                />

                <Controller
                  name="internalComment"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <TextField
                        label={t('common.internal_comment')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        rows={5}
                        ref={ref}
                      />
                    );
                  }}
                />
              </Stack>
            </Panel>

            <Panel title={t('companies.finances.title')}>
              <Stack gap={2}>
                <Controller
                  name="billingInfo"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextField
                      label={t('companies.billingInfo.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      rows={5}
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name="accountantLanguage"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SelectInput
                        label={t('companies.accountantLanguage.label')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={uppercaseContentLanguages.map((language) => ({
                          label: formatLanguage(i18n.language, language),
                          value: language,
                        }))}
                        ref={ref}
                      />
                    );
                  }}
                />

                <Stack gap={1}>
                  <Typography variant="p1" color="textSecondary">
                    {t('companies.accountantEmail.label')}
                  </Typography>
                  <SortableList
                    key={JSON.stringify(
                      accountantEmailsFields.map((field) => field.id),
                    )}
                    disabled
                    items={accountantEmailsFields.map((field, i) => ({
                      id: field.id,
                      content: (
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="stretch"
                          py={0.5}
                          spacing={2}
                          key={field.id}
                        >
                          <Box flex={1}>
                            <Controller
                              name={`accountantEmails.${i}.email`}
                              control={control}
                              render={({
                                field: { name, value, onChange, ref },
                              }) => (
                                <TextInput
                                  label={t('common.email')}
                                  name={name}
                                  value={value}
                                  onChange={onChange}
                                  type="email"
                                  sx={{ width: '100%' }}
                                  required
                                  ref={ref}
                                />
                              )}
                            />
                          </Box>

                          <Box flexShrink={0}>
                            <Button
                              variant="neutral-outline"
                              size="md"
                              icon={Delete}
                              onClick={() => removeAccountantEmail(i)}
                              disabled={accountantEmailsFields.length <= 1}
                              title={t('common.remove')}
                            />
                          </Box>
                        </Stack>
                      ),
                    }))}
                  />

                  <Box>
                    <Button
                      label={t('common.add')}
                      variant="tertiary"
                      onClick={() => {
                        appendAccountantEmail({ email: '' });
                      }}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Panel>

            <Panel title={t('common.settings')}>
              <Stack gap={2}>
                <Controller
                  name="depositManagementMethod"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SelectInput
                        label={t('deposits.depositManagementMethod.label')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={depositManagementMethods.map((method) => ({
                          label: t(`deposits.method.${method}`),
                          value: method,
                        }))}
                        required
                        ref={ref}
                      />
                    );
                  }}
                />

                <Controller
                  name="useExternalId"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <SwitchInput
                      label={t('clients.useExternalId.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name="sendEmployeeWelcomeMessage"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <SwitchInput
                      label={t('clients.sendEmployeeWelcomeMessage.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      ref={ref}
                    />
                  )}
                />
              </Stack>
            </Panel>

            <Panel title={t('clients.auth.label')}>
              <Stack gap={2}>
                <Controller
                  name="authProvider"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SelectInput
                        label={t('clients.auth.provider.label')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={authProviders.map((provider) => ({
                          label: t(`auth.providers.${provider}`),
                          value: provider,
                        }))}
                        ref={ref}
                      />
                    );
                  }}
                />

                {authProvider === 'entraId' ? (
                  <Controller
                    name="authProviderName"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <TextField
                          label={t('clients.auth.providerName.label')}
                          name={name}
                          value={value}
                          onChange={onChange}
                          ref={ref}
                        />
                      );
                    }}
                  />
                ) : null}

                <UnlinkAppUsers clientId={clientId} />
              </Stack>
            </Panel>
          </Stack>

          <Footer visible={isDirty}>
            <Stack direction="row" justifyContent="end" gap={1}>
              <Button
                label={t('common.cancel')}
                variant="neutral-outline"
                onClick={() => {
                  reset(undefined, { keepDirty: false });
                }}
              />
              <Button
                label={t('common.confirm_changes')}
                type="submit"
                disabled={isUpdating}
              />
            </Stack>
          </Footer>
        </form>
      </Stack>

      <DataLossPrompt when={isDirty} />
    </>
  );
};
